/**
 * Inputs for a duty & tax estimate. Shared by the standalone estimate page and
 * the moderator's per-deal trade terms. Destinations with curated data come
 * from `GET /tradeCosts/countries/`; any other destination still gets an
 * estimate, as a wide range with low confidence.
 */
import { type FormEvent, type ReactNode, useState } from "react";
import { useTranslation } from "react-i18next";
import { formatNumber } from "../../i18n/format";
import { normaliseHsCode } from "../../lib/hsCodes";
import { countryOptions } from "../../lib/regionOptions";
import { useHscodesCodesRetrieve } from "../../services/hscodes/generated/hs-codes/hs-codes";
import {
  type CountryTaxProfile,
  type EstimateRequestRequest,
  IncotermEnum,
} from "../../services/tradecosts/generated/model";
import { useTradeCostsCountriesList } from "../../services/tradecosts/generated/trade-costs/trade-costs";
import "./TradeEstimate.css";

// Currencies of the live corridor plus the majors; ISO codes are data.
const CURRENCIES = ["CAD", "USD", "EUR", "AED", "SAR", "QAR", "KWD", "BHD", "OMR", "GBP"] as const;
const INCOTERMS = Object.values(IncotermEnum);

export interface TradeEstimateValues {
  originCountry: string;
  destinationCountry: string;
  destinationSubdivision: string;
  hsCode: string;
  customsValue: string;
  currency: string;
  incoterm: string;
  freight: string;
  insurance: string;
  importerVatRegistered: "" | "yes" | "no";
  quantity: string;
  unit: string;
}

export const EMPTY_ESTIMATE_VALUES: TradeEstimateValues = {
  originCountry: "CA",
  destinationCountry: "AE",
  destinationSubdivision: "",
  hsCode: "",
  customsValue: "",
  currency: "CAD",
  incoterm: "",
  freight: "",
  insurance: "",
  importerVatRegistered: "",
  quantity: "",
  unit: "",
};

/** Form values → API request (blank optional fields are left out). */
export function toEstimateRequest(v: TradeEstimateValues): EstimateRequestRequest {
  return {
    originCountry: v.originCountry,
    destinationCountry: v.destinationCountry,
    destinationSubdivision: v.destinationSubdivision || undefined,
    hsCode: normaliseHsCode(v.hsCode),
    customsValue: v.customsValue.trim(),
    currency: v.currency,
    // Unknown = left out: the API takes a blank Incoterm but not null.
    incoterm: (v.incoterm || undefined) as EstimateRequestRequest["incoterm"],
    freight: v.freight.trim() || null,
    insurance: v.insurance.trim() || null,
    importerVatRegistered: v.importerVatRegistered === "" ? null : v.importerVatRegistered === "yes",
    quantity: v.quantity.trim() || null,
    unit: v.unit.trim() || undefined,
  };
}

const TradeEstimateForm = ({
  initial,
  submitLabel,
  busy = false,
  requireValue = true,
  onSubmit,
  extra,
}: {
  initial: TradeEstimateValues;
  submitLabel: string;
  busy?: boolean;
  /** The per-deal terms may be saved before the value is known. */
  requireValue?: boolean;
  onSubmit: (values: TradeEstimateValues) => void;
  extra?: ReactNode;
}) => {
  const { t } = useTranslation(["trade", "common"]);
  const [v, setV] = useState<TradeEstimateValues>(initial);
  const { data: countriesData } = useTradeCostsCountriesList();
  const profiles = (countriesData as CountryTaxProfile[] | undefined) ?? [];
  const profile = profiles.find((p) => p.country === v.destinationCountry);
  const code = normaliseHsCode(v.hsCode);
  const codeLookup = useHscodesCodesRetrieve(code, { query: { enabled: code.length >= 2, retry: false } });

  const set = <K extends keyof TradeEstimateValues>(key: K, value: TradeEstimateValues[K]) =>
    setV((prev) => ({ ...prev, [key]: value }));

  const submit = (e: FormEvent) => {
    e.preventDefault();
    onSubmit(v);
  };

  return (
    <form className="te-form" onSubmit={submit}>
      <div className="te-form__grid">
        <label>
          {t("form.origin")}
          <select
            className="te-input"
            value={v.originCountry}
            onChange={(e) => set("originCountry", e.target.value)}
            required
          >
            {countryOptions().map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </label>
        <label>
          {t("form.destination")}
          <select
            className="te-input"
            value={v.destinationCountry}
            onChange={(e) => {
              set("destinationCountry", e.target.value);
              set("destinationSubdivision", "");
            }}
            required
          >
            {profiles.length > 0 ? (
              <optgroup label={t("form.withData")}>
                {profiles.map((p) => (
                  <option key={p.country} value={p.country}>
                    {p.name}
                  </option>
                ))}
              </optgroup>
            ) : null}
            <optgroup label={t("form.otherCountries")}>
              {countryOptions()
                .filter((o) => !profiles.some((p) => p.country === o.value))
                .map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
            </optgroup>
          </select>
          <span className="te-field-hint">
            {profile
              ? t("form.destinationTax", {
                  name: profile.vatName || t("form.vat"),
                  rate: formatNumber(profile.vatStandard ?? 0, { maximumFractionDigits: 2 }),
                  basis: t(
                    `estimate.basis.${profile.valuationBasis === "cif" ? "cif" : profile.valuationBasis === "transaction" ? "transaction" : "unknown"}`,
                  ),
                })
              : t("form.noData")}
          </span>
        </label>
        {profile && profile.subdivisions.length > 0 ? (
          <label>
            {t("form.province")}
            <select
              className="te-input"
              value={v.destinationSubdivision}
              onChange={(e) => set("destinationSubdivision", e.target.value)}
            >
              <option value="">{t("form.provinceUnknown")}</option>
              {profile.subdivisions.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </label>
        ) : null}
        <label>
          {t("form.hsCode")}
          <input
            className="te-input"
            value={v.hsCode}
            placeholder="170220"
            onChange={(e) => set("hsCode", e.target.value)}
            required
          />
          <span className="te-field-hint">
            {code.length >= 2 && codeLookup.data
              ? codeLookup.data.description
              : code.length >= 2 && codeLookup.isError
                ? t("form.hsUnknown")
                : t("form.hsHint")}
          </span>
        </label>
        <label>
          {t("form.value")}
          <input
            className="te-input"
            inputMode="decimal"
            value={v.customsValue}
            onChange={(e) => set("customsValue", e.target.value)}
            required={requireValue}
          />
        </label>
        <label>
          {t("form.currency")}
          <select className="te-input" value={v.currency} onChange={(e) => set("currency", e.target.value)}>
            {CURRENCIES.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </label>
        <label>
          {t("form.incoterm")}
          <select className="te-input" value={v.incoterm} onChange={(e) => set("incoterm", e.target.value)}>
            <option value="">{t("form.incotermUnknown")}</option>
            {INCOTERMS.map((i) => (
              <option key={i} value={i}>
                {i}
              </option>
            ))}
          </select>
        </label>
        <label>
          {t("form.freight")}
          <input
            className="te-input"
            inputMode="decimal"
            value={v.freight}
            onChange={(e) => set("freight", e.target.value)}
          />
        </label>
        <label>
          {t("form.insurance")}
          <input
            className="te-input"
            inputMode="decimal"
            value={v.insurance}
            onChange={(e) => set("insurance", e.target.value)}
          />
        </label>
        <label>
          {t("form.vatRegistered")}
          <select
            className="te-input"
            value={v.importerVatRegistered}
            onChange={(e) =>
              set("importerVatRegistered", e.target.value as TradeEstimateValues["importerVatRegistered"])
            }
          >
            <option value="">{t("form.vatUnknown")}</option>
            <option value="yes">{t("common:labels.yes")}</option>
            <option value="no">{t("common:labels.no")}</option>
          </select>
        </label>
      </div>
      <p className="te-field-hint">{t("form.optionalHint")}</p>
      {extra}
      <div>
        <button type="submit" className="te-btn" disabled={busy}>
          {busy ? t("form.working") : submitLabel}
        </button>
      </div>
    </form>
  );
};

export default TradeEstimateForm;
